import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, 
  Zap, 
  Star, 
  Check, 
  ArrowRight,
  Sparkles,
  Workflow,
  Bot,
  Users
} from 'lucide-react';
import { motion } from 'framer-motion';

interface SubscriptionCTAProps {
  feature: string;
  requiredPlan?: 'pro' | 'enterprise' | 'creator';
  onUpgrade?: () => void;
  className?: string;
}

const planFeatures = {
  pro: {
    name: 'Pro',
    price: '$29',
    icon: Zap,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    features: [
      'Advanced workflow designer',
      'Custom agent creation',
      'API integrations',
      'Priority support'
    ]
  },
  enterprise: {
    name: 'Enterprise',
    price: '$99',
    icon: Crown,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    features: [
      'Everything in Pro',
      'Team collaboration',
      'Advanced analytics',
      'White-label solutions',
      'Dedicated support'
    ]
  },
  creator: {
    name: 'Creator',
    price: '$19',
    icon: Sparkles,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    features: [
      'Enhanced agent marketplace',
      'Revenue sharing',
      'Creator tools',
      'Community access'
    ]
  }
};

const featureIcons = {
  'workflow': Workflow,
  'agent': Bot,
  'user_management': Users,
  'analytics': Star
};

export function SubscriptionCTA({ 
  feature, 
  requiredPlan = 'pro', 
  onUpgrade,
  className 
}: SubscriptionCTAProps) {
  const plan = planFeatures[requiredPlan];
  const PlanIcon = plan.icon;
  const FeatureIcon = featureIcons[feature as keyof typeof featureIcons] || Zap;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default action - navigate to subscription page
      window.location.href = '/subscription';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <Card className={`${plan.borderColor} ${plan.bgColor} border-2`}>
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3
              }}
              className={`p-3 rounded-full ${plan.bgColor} ${plan.borderColor} border`}
            >
              <FeatureIcon className={`h-8 w-8 ${plan.color}`} />
            </motion.div>
          </div>
          
          <CardTitle className="text-xl mb-2">
            Unlock {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </CardTitle>
          
          <CardDescription className="text-base">
            This feature requires a {plan.name} subscription to access advanced capabilities.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Plan Badge */}
          <div className="flex justify-center">
            <Badge 
              variant="secondary" 
              className={`${plan.color} ${plan.bgColor} border-current px-4 py-1`}
            >
              <PlanIcon className="h-4 w-4 mr-2" />
              {plan.name} Plan - {plan.price}/month
            </Badge>
          </div>

          {/* Features List */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-center mb-3">
              What you'll get:
            </h4>
            <div className="grid gap-2">
              {plan.features.map((planFeature, index) => (
                <motion.div
                  key={planFeature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <div className={`p-1 rounded-full ${plan.bgColor}`}>
                    <Check className={`h-3 w-3 ${plan.color}`} />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {planFeature}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="pt-4"
          >
            <Button 
              onClick={handleUpgrade}
              className={`w-full ${plan.color.replace('text-', 'bg-')} hover:opacity-90 text-white`}
              size="lg"
            >
              <Crown className="h-4 w-4 mr-2" />
              Upgrade to {plan.name}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>

          {/* Trust indicators */}
          <div className="text-center pt-2">
            <p className="text-xs text-muted-foreground">
              ✨ 7-day free trial • 📞 24/7 support • 🔒 Cancel anytime
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Specific CTA components for different features
export function WorkflowSubscriptionCTA({ onUpgrade, className }: { 
  onUpgrade?: () => void; 
  className?: string; 
}) {
  return (
    <SubscriptionCTA
      feature="workflow"
      requiredPlan="enterprise"
      onUpgrade={onUpgrade}
      className={className}
    />
  );
}

export function AgentCreationCTA({ onUpgrade, className }: { 
  onUpgrade?: () => void; 
  className?: string; 
}) {
  return (
    <SubscriptionCTA
      feature="agent"
      requiredPlan="creator"
      onUpgrade={onUpgrade}
      className={className}
    />
  );
}

export function AnalyticsCTA({ onUpgrade, className }: { 
  onUpgrade?: () => void; 
  className?: string; 
}) {
  return (
    <SubscriptionCTA
      feature="analytics"
      requiredPlan="pro"
      onUpgrade={onUpgrade}
      className={className}
    />
  );
}
